import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@plato/ui/components/select';
import { cn } from '@plato/ui/util/classes';
import React, { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type DashboardChatTabsItemProps = {
    label: string;
    tabId: string;
    children?: ReactNode | ReactNode[];
    className?: string;
    detailBlock?: React.ReactElement | React.ReactElement[] | null | string;
};

export function DashboardChatTabsItem({ children, className = '' }: DashboardChatTabsItemProps) {
    return <div className={cn('flex', className)}>{children}</div>;
}

type DashboardChatTabsProps = {
    className?: string;
    defaultTab?: string;
    onTabChange?: (tabId: string) => void;
    actionGroup?: React.ReactElement | React.ReactElement[] | null | string;
} & React.PropsWithChildren;

export function DashboardChatTabs({
    children,
    actionGroup,
    className,
    defaultTab,
    onTabChange,
}: DashboardChatTabsProps) {
    const { t } = useTranslation('dashboard');
    const [selectedTabKey, setSelectedTabKey] = useState<string | null>(defaultTab ?? null);

    const cleanChildrenArray = React.Children.toArray(children).filter(
        (child): child is React.ReactElement<DashboardChatTabsItemProps> =>
            React.isValidElement(child) && child.type === DashboardChatTabsItem,
    );

    const isActiveTab = (index: number, tabId: string): boolean => {
        return (
            (selectedTabKey === null && index === 0) || (selectedTabKey !== null && selectedTabKey === tabId)
        );
    };

    useEffect(() => {
        if (onTabChange && selectedTabKey) {
            onTabChange(selectedTabKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTabKey]);

    const tabs: SelectOption[] = cleanChildrenArray.map((child) => ({
        value: child.props.tabId,
        label: child.props.label,
    }));
    const activeTabKey = selectedTabKey ?? tabs[0]?.value;

    return (
        <div className={cn('flex w-full flex-col gap-3', className)}>
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                <Select
                    title={t('chart_tabs__metric_label')}
                    items={tabs}
                    value={activeTabKey}
                    onValueChange={(tab) => {
                        if (tab) {
                            setSelectedTabKey(tab);
                        }
                    }}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectList>
                            {(tab: SelectOption) => <SelectItem key={tab.value} value={tab} />}
                        </SelectList>
                    </SelectContent>
                </Select>
                <div className="flex flex-auto grow flex-row sm:-order-1">
                    {cleanChildrenArray.map((child, index) => {
                        if (isActiveTab(index, child.props.tabId)) {
                            return child.props.detailBlock ?? null;
                        } else {
                            return null;
                        }
                    })}
                </div>
                {actionGroup ?? null}
            </div>
            {cleanChildrenArray.map((child, index) => {
                if (isActiveTab(index, child.props.tabId)) {
                    return child;
                } else {
                    return null;
                }
            })}
        </div>
    );
}
